import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tooltip,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  PAGES,
  REPORT_CATEGORIES,
  REPORT_SYSTEM_PERMISSIONS,
  SUBPAGES,
  getPagesByModule,
  getPagesForBuilder,
  getReportCategoriesForBuilder,
  getReportSystemPermissionsForBuilder,
  getSubpagesForBuilder,
  resolveEnabledModules,
  isModuleEnabled,
  getAllModules,
} from "../config/permissions-registry";
import {
  categorySource,
  getMetadataSystem,
  byMetadataConfigOrder,
} from "../config/metadata-systems";
import LambdaFetch from "../functions/FetchFromLambda";

const COMMENT_ACTIONS = [
  { key: 'view',   label: 'View',   description: 'See the Comments button and read comments' },
  { key: 'create', label: 'Create', description: 'Add new comments' },
  { key: 'edit',   label: 'Edit',   description: 'Edit own comments (author-only, enforced server-side)' },
  { key: 'delete', label: 'Delete', description: 'Delete any comment (moderation; authors can always delete their own)' },
];

// Clean up permissions object - preserve true and false values, remove undefined
const cleanPermissions = (perms) => {
  const cleaned = {};
  
  // Clean reportCategory - preserve both true and false
  if (perms.reportCategory && Object.keys(perms.reportCategory).length > 0) {
    const cleanedCategories = {};
    Object.keys(perms.reportCategory).forEach(key => {
      const value = perms.reportCategory[key];
      if (value === true || value === false) {
        cleanedCategories[key] = value;
      }
    });
    if (Object.keys(cleanedCategories).length > 0) {
      cleaned.reportCategory = cleanedCategories;
    }
  }

  // Clean reportSystem - preserve both true and false
  if (perms.reportSystem && Object.keys(perms.reportSystem).length > 0) {
    const cleanedSystems = {};
    Object.keys(perms.reportSystem).forEach(key => {
      const value = perms.reportSystem[key];
      if (value === true || value === false) {
        cleanedSystems[key] = value;
      }
    });
    if (Object.keys(cleanedSystems).length > 0) {
      cleaned.reportSystem = cleanedSystems;
    }
  }
  
  // Clean subpage - preserve both true and false
  if (perms.subpage && Object.keys(perms.subpage).length > 0) {
    const cleanedSubpages = {};
    Object.keys(perms.subpage).forEach(module => {
      const moduleSubpages = perms.subpage[module];
      if (moduleSubpages && typeof moduleSubpages === 'object') {
        const cleanedModule = {};
        Object.keys(moduleSubpages).forEach(key => {
          const value = moduleSubpages[key];
          if (value === true || value === false) {
            cleanedModule[key] = value;
          }
        });
        if (Object.keys(cleanedModule).length > 0) {
          cleanedSubpages[module] = cleanedModule;
        }
      }
    });
    if (Object.keys(cleanedSubpages).length > 0) {
      cleaned.subpage = cleanedSubpages;
    }
  }
  
  // Clean page - preserve both true and false
  if (perms.page && Object.keys(perms.page).length > 0) {
    const cleanedPages = {};
    Object.keys(perms.page).forEach(key => {
      const value = perms.page[key];
      if (value === true || value === false) {
        cleanedPages[key] = value;
      }
    });
    if (Object.keys(cleanedPages).length > 0) {
      cleaned.page = cleanedPages;
    }
  }
  
  // Clean metadata - preserve both true and false
  if (perms.metadata && Object.keys(perms.metadata).length > 0) {
    const cleanedMetadata = {};
    Object.keys(perms.metadata).forEach(key => {
      const value = perms.metadata[key];
      if (isPermissionValue(value)) {
        cleanedMetadata[key] = value;
      } else if (value && typeof value === "object" && !Array.isArray(value)) {
        const cleanedSource = {};
        Object.keys(value).forEach(metadataKey => {
          const sourceValue = value[metadataKey];
          if (metadataKey !== "*" && isPermissionValue(sourceValue)) {
            cleanedSource[metadataKey] = sourceValue;
          }
        });
        if (Object.keys(cleanedSource).length > 0) {
          cleanedMetadata[key] = cleanedSource;
        }
      }
    });
    if (Object.keys(cleanedMetadata).length > 0) {
      cleaned.metadata = cleanedMetadata;
    }
  }

  // Clean comments - preserve true and false per action
  if (perms.comments && Object.keys(perms.comments).length > 0) {
    const cleanedComments = {};
    Object.keys(perms.comments).forEach(key => {
      const value = perms.comments[key];
      if (value === true || value === false) {
        cleanedComments[key] = value;
      }
    });
    if (Object.keys(cleanedComments).length > 0) {
      cleaned.comments = cleanedComments;
    }
  }

  return cleaned;
};

const isPermissionValue = (value) => value === true || value === false;

const getCategoryName = (category) => category.NAME || category.name;

const getCategoryDescription = (category) =>
  category.DESCRIPTION || category.description || getCategoryName(category);

const metadataEntryForSource = (metadata, sourceKey) => {
  const entry = metadata?.[sourceKey];
  return entry && typeof entry === "object" && !Array.isArray(entry) ? entry : {};
};

/**
 * PermissionBuilder Component
 * 
 * Visual builder for role permissions that uses the permissions registry.
 * Replaces manual JSON editing with a user-friendly interface.
 * 
 * @param {Object} value - Current permissions object
 * @param {Function} onChange - Callback when permissions change
 * @param {Object} credentials - User credentials for API calls
 */
export default function PermissionBuilder({ value = {}, onChange, credentials }) {
  const [activeTab, setActiveTab] = useState(0);
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [jsonPreview, setJsonPreview] = useState("");
  const previousCleanedRef = useRef(null);
  const isInitialMount = useRef(true);
  const [metadataCategories, setMetadataCategories] = useState([]);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [enabledModules, setEnabledModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(true);

  // Initialize permissions from value prop
  const [permissions, setPermissions] = useState(() => {
    // Ensure we have the correct structure
    return {
      reportCategory: value.reportCategory || {},
      reportSystem: value.reportSystem || {},
      subpage: value.subpage || {},
      page: value.page || {},
      metadata: value.metadata || {},
      comments: value.comments || {},
    };
  });

  // Track previous value to detect actual changes
  const previousValueRef = useRef(JSON.stringify(value));

  // Fetch enabled modules on mount
  useEffect(() => {
    const fetchEnabledModules = () => {
      // Try to get from credentials first (passed from backend)
      const rawModules = credentials?.clientModules || credentials?.user?.clientModules;
      
      if (rawModules) {
        const resolved = resolveEnabledModules(rawModules);
        setEnabledModules(resolved);
        setModulesLoading(false);
        return;
      }
      
      // Fallback: try to fetch from API endpoint
      const fetchFromAPI = async () => {
        if (!credentials || !credentials.user || !credentials.user.accessToken) {
          // If no credentials, default to all modules (fail open)
          setEnabledModules(getAllModules());
          setModulesLoading(false);
          return;
        }

        try {
          const resp = await LambdaFetch(
            'dynamo/config/enabled-modules',
            'get',
            credentials.user.accessToken,
            null,
            '',
            credentials
          );
          
          let modules = resp?.data?.enabledModules || [];
          
          // If wildcard or empty, get all modules from registry
          if (modules.length === 0 || (modules.length === 1 && modules[0] === '*')) {
            modules = getAllModules();
          }
          
          setEnabledModules(modules);
        } catch (e) {
          console.error('Error fetching enabled modules:', e);
          // Fail open: show all modules if API fails
          setEnabledModules(getAllModules());
        } finally {
          setModulesLoading(false);
        }
      };
      
      fetchFromAPI();
    };

    fetchEnabledModules();
  }, [credentials]);

  // Fetch metadata categories on mount
  useEffect(() => {
    const fetchMetadataCategories = async () => {
      if (!credentials || !credentials.user || !credentials.user.accessToken) {
        return;
      }

      setMetadataLoading(true);
      try {
        const resp = await LambdaFetch(
          'reports-athena',
          'post',
          credentials.user.accessToken,
          JSON.stringify({
            action: 'get-config-file',
            file_name: 'metadata-headers.json'
          }),
          '',
          credentials
        );

        const categories = resp?.data?.metadataCateogries || resp?.data?.metadataCategories || [];
        // Sort by DESCRIPTION
        const sortedCategories = categories.sort((a, b) => {
          const descA = a.DESCRIPTION || '';
          const descB = b.DESCRIPTION || '';
          return descA.localeCompare(descB);
        });

        setMetadataCategories(sortedCategories);
      } catch (e) {
        console.error('Error fetching metadata categories:', e);
        setMetadataCategories([]);
      } finally {
        setMetadataLoading(false);
      }
    };

    fetchMetadataCategories();
  }, [credentials]);

  // Update local state when value prop changes (but only if it's different)
  useEffect(() => {
    const newPermissions = {
      reportCategory: value.reportCategory || {},
      reportSystem: value.reportSystem || {},
      subpage: value.subpage || {},
      page: value.page || {},
      metadata: value.metadata || {},
      comments: value.comments || {},
    };
    
    // Only update if the value actually changed (deep comparison via JSON)
    const newValueJson = JSON.stringify(newPermissions);
    const previousValueJson = previousValueRef.current;
    
    if (previousValueJson !== newValueJson) {
      previousValueRef.current = newValueJson;
      setPermissions(newPermissions);
    }
  }, [value]);

  // Update JSON preview and notify parent whenever permissions change
  useEffect(() => {
    const cleaned = cleanPermissions(permissions);
    const cleanedJson = JSON.stringify(cleaned);
    
    // Update JSON preview
    setJsonPreview(JSON.stringify(cleaned, null, 2));
    
    // Only call onChange if the cleaned value actually changed
    // Skip on initial mount to avoid unnecessary calls
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousCleanedRef.current = cleanedJson;
      // Still call onChange on mount to ensure parent has the cleaned value
      if (onChange) {
        onChange(cleaned);
      }
    } else if (previousCleanedRef.current !== cleanedJson) {
      previousCleanedRef.current = cleanedJson;
      // Notify parent of changes with cleaned permissions
      if (onChange) {
        onChange(cleaned);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions]); // Removed onChange from dependencies to prevent infinite loops

  // Group pages by module for better organization (only pages that should show in builder)
  // Filter by enabled modules and sort pages within each module alphabetically by displayName
  const pagesByModule = useMemo(() => {
    if (modulesLoading) {
      return {}; // Don't show anything until modules are loaded
    }
    
    const grouped = {};
    const pagesForBuilder = getPagesForBuilder();
    pagesForBuilder.forEach((page) => {
      const module = page.module || "Other";
      
      // Only include if module is enabled (or page has no module)
      if (!page.module || isModuleEnabled(module, enabledModules)) {
        if (!grouped[module]) {
          grouped[module] = [];
        }
        grouped[module].push(page);
      }
    });
    // Sort pages within each module alphabetically by displayName
    Object.keys(grouped).forEach((module) => {
      grouped[module].sort((a, b) => 
        a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })
      );
    });
    return grouped;
  }, [enabledModules, modulesLoading]);

  const metadataGroups = useMemo(() => {
    const groupsBySource = new Map();

    metadataCategories.forEach((category) => {
      const categoryName = getCategoryName(category);
      if (!categoryName) return;

      const sourceKey = categorySource(category);
      const system = getMetadataSystem(sourceKey);
      const existing = groupsBySource.get(system.sourceKey) || {
        system,
        categories: [],
      };

      existing.categories.push(category);
      groupsBySource.set(system.sourceKey, existing);
    });

    return Array.from(groupsBySource.values())
      .map((group) => ({
        ...group,
        categories: group.categories.sort((a, b) =>
          getCategoryDescription(a).localeCompare(getCategoryDescription(b), undefined, {
            sensitivity: "base",
          })
        ),
      }))
      .sort((a, b) => byMetadataConfigOrder(a.system, b.system));
  }, [metadataCategories]);

  // Handle page permission change (true/false/undefined)
  const handlePageChange = (pageKey, value) => {
    setPermissions((prev) => {
      const newPage = { ...prev.page };
      
      if (value === "true") {
        newPage[pageKey] = true;
      } else if (value === "false") {
        newPage[pageKey] = false;
      } else {
        // "notset" - remove from object
        delete newPage[pageKey];
      }
      
      return {
        ...prev,
        page: newPage,
      };
    });
  };

  // Get page permission state (for Select dropdown)
  const getPagePermissionState = (pageKey) => {
    const value = permissions.page?.[pageKey];
    if (value === true) return "true";
    if (value === false) return "false";
    return "notset";
  };

  // Handle page wildcard toggle
  const handlePageWildcardToggle = (checked) => {
    setPermissions((prev) => {
      if (checked) {
        // Set wildcard - this grants all pages
        return {
          ...prev,
          page: { "*": true },
        };
      } else {
        // Remove wildcard
        const newPage = { ...prev.page };
        delete newPage["*"];
        return {
          ...prev,
          page: newPage,
        };
      }
    });
  };

  // Handle report category permission change (true/false/undefined)
  const handleReportCategoryChange = (categoryKey, value) => {
    setPermissions((prev) => {
      const newCategory = { ...prev.reportCategory };
      
      if (value === "true") {
        newCategory[categoryKey] = true;
      } else if (value === "false") {
        newCategory[categoryKey] = false;
      } else {
        // "notset" - remove from object
        delete newCategory[categoryKey];
      }
      
      return {
        ...prev,
        reportCategory: newCategory,
      };
    });
  };

  // Get report category permission state (for Select dropdown)
  const getReportCategoryPermissionState = (categoryKey) => {
    const value = permissions.reportCategory?.[categoryKey];
    if (value === true) return "true";
    if (value === false) return "false";
    return "notset";
  };

  // Handle report category wildcard toggle
  const handleReportCategoryWildcardToggle = (checked) => {
    setPermissions((prev) => {
      if (checked) {
        // Set wildcard - this grants all report categories
        return {
          ...prev,
          reportCategory: { "*": true },
        };
      } else {
        // Remove wildcard
        const newCategory = { ...prev.reportCategory };
        delete newCategory["*"];
        return {
          ...prev,
          reportCategory: newCategory,
        };
      }
    });
  };

  // Handle subpage permission change (true/false/undefined)
  const handleSubpageChange = (module, subpageKey, value) => {
    setPermissions((prev) => {
      const newSubpage = { ...prev.subpage };
      if (!newSubpage[module]) {
        newSubpage[module] = {};
      }
      const moduleSubpages = { ...newSubpage[module] };
      
      if (value === "true") {
        moduleSubpages[subpageKey] = true;
      } else if (value === "false") {
        moduleSubpages[subpageKey] = false;
      } else {
        // "notset" - remove from object
        delete moduleSubpages[subpageKey];
      }
      
      // Remove module entry if empty
      if (Object.keys(moduleSubpages).length === 0) {
        delete newSubpage[module];
      } else {
        newSubpage[module] = moduleSubpages;
      }
      
      return {
        ...prev,
        subpage: newSubpage,
      };
    });
  };

  // Get subpage permission state (for Select dropdown)
  const getSubpagePermissionState = (module, subpageKey) => {
    const moduleSubpages = permissions.subpage?.[module];
    if (!moduleSubpages) return "notset";
    // Check if wildcard is set
    if (moduleSubpages["*"] === true) return "true";
    const value = moduleSubpages[subpageKey];
    if (value === true) return "true";
    if (value === false) return "false";
    return "notset";
  };

  // Handle wildcard toggle for subpages
  const handleSubpageWildcardToggle = (module, checked) => {
    setPermissions((prev) => {
      const newSubpage = { ...prev.subpage };
      if (!newSubpage[module]) {
        newSubpage[module] = {};
      }
      
      if (checked) {
        // Set wildcard - this grants all subpages for the module
        newSubpage[module] = { "*": true };
      } else {
        // Remove wildcard
        delete newSubpage[module]["*"];
        // Remove module entry if empty
        if (Object.keys(newSubpage[module]).length === 0) {
          delete newSubpage[module];
        }
      }
      
      return {
        ...prev,
        subpage: newSubpage,
      };
    });
  };

  // Check if a page is selected
  const isPageSelected = (pageKey) => {
    // Check if wildcard is set
    if (permissions.page?.["*"] === true) return true;
    // Check if specific page is set
    return permissions.page?.[pageKey] === true;
  };

  // Check if page wildcard is selected
  const isPageWildcardSelected = () => {
    return permissions.page?.["*"] === true;
  };

  // Check if a report category is selected
  const isReportCategorySelected = (categoryKey) => {
    // Check if wildcard is set
    if (permissions.reportCategory?.["*"] === true) return true;
    // Check if specific category is set
    return permissions.reportCategory?.[categoryKey] === true;
  };

  // Check if report category wildcard is selected
  const isReportCategoryWildcardSelected = () => {
    return permissions.reportCategory?.["*"] === true;
  };

  // Handle report system permission change (true/false/undefined)
  const handleReportSystemChange = (systemKey, value) => {
    setPermissions((prev) => {
      const newSystem = { ...prev.reportSystem };
      if (value === "true") {
        newSystem[systemKey] = true;
      } else if (value === "false") {
        newSystem[systemKey] = false;
      } else {
        delete newSystem[systemKey];
      }
      return { ...prev, reportSystem: newSystem };
    });
  };

  // Get report system permission state (for Select dropdown)
  const getReportSystemPermissionState = (systemKey) => {
    const value = permissions.reportSystem?.[systemKey];
    if (value === true) return "true";
    if (value === false) return "false";
    return "notset";
  };

  // Handle report system wildcard toggle
  const handleReportSystemWildcardToggle = (checked) => {
    setPermissions((prev) => {
      if (checked) {
        return { ...prev, reportSystem: { "*": true } };
      } else {
        const newSystem = { ...prev.reportSystem };
        delete newSystem["*"];
        return { ...prev, reportSystem: newSystem };
      }
    });
  };

  // Check if a report system is selected
  const isReportSystemSelected = (systemKey) => {
    if (permissions.reportSystem?.["*"] === true) return true;
    return permissions.reportSystem?.[systemKey] === true;
  };

  // Check if report system wildcard is selected
  const isReportSystemWildcardSelected = () => {
    return permissions.reportSystem?.["*"] === true;
  };

  // Check if a subpage is selected
  const isSubpageSelected = (module, subpageKey) => {
    const moduleSubpages = permissions.subpage?.[module];
    if (!moduleSubpages) return false;
    // Check if wildcard is set
    if (moduleSubpages["*"] === true) return true;
    // Check if specific subpage is set
    return moduleSubpages[subpageKey] === true;
  };

  // Check if wildcard is set for a module
  const isSubpageWildcardSelected = (module) => {
    return permissions.subpage?.[module]?.["*"] === true;
  };

  const getSelectableMetadataCategoryCount = () => {
    return metadataCategories.filter((category) => Boolean(getCategoryName(category))).length;
  };

  const getAllowedMetadataCategoryCount = (metadata = permissions.metadata || {}) => {
    if (metadata["*"] === true) {
      return getSelectableMetadataCategoryCount();
    }

    return metadataCategories.filter((category) => {
      const categoryName = getCategoryName(category);
      if (!categoryName) return false;

      const sourceMetadata = metadataEntryForSource(metadata, categorySource(category));
      return sourceMetadata[categoryName] === true || metadata[categoryName] === true;
    }).length;
  };

  const wouldAllowAllMetadataCategories = (metadata, sourceKey, metadataKey) => {
    const totalCategories = getSelectableMetadataCategoryCount();
    if (totalCategories === 0) return false;

    const currentValue =
      metadataEntryForSource(metadata, sourceKey)[metadataKey] ?? metadata[metadataKey];
    if (currentValue === true) return false;

    return getAllowedMetadataCategoryCount(metadata) >= totalCategories - 1;
  };

  // Handle metadata permission change (true/false/undefined)
  const handleMetadataChange = (sourceKey, metadataKey, value) => {
    setPermissions((prev) => {
      if (
        value === "true" &&
        wouldAllowAllMetadataCategories(prev.metadata || {}, sourceKey, metadataKey)
      ) {
        return prev;
      }

      const newMetadata = { ...prev.metadata };
      const sourceMetadata = {
        ...metadataEntryForSource(newMetadata, sourceKey),
      };
      
      if (value === "true") {
        sourceMetadata[metadataKey] = true;
      } else if (value === "false") {
        sourceMetadata[metadataKey] = false;
      } else {
        // "notset" - remove from object
        delete sourceMetadata[metadataKey];
      }

      // Remove old flat metadata entries for this category once it is edited by source.
      delete newMetadata[metadataKey];

      if (Object.keys(sourceMetadata).length === 0) {
        delete newMetadata[sourceKey];
      } else {
        newMetadata[sourceKey] = sourceMetadata;
      }
      
      return {
        ...prev,
        metadata: newMetadata,
      };
    });
  };

  // Get metadata permission state (for Select dropdown)
  const getMetadataPermissionValue = (sourceKey, metadataKey) => {
    const metadata = permissions.metadata || {};

    if (isPermissionValue(metadata["*"])) return metadata["*"];

    const sourceMetadata = metadataEntryForSource(metadata, sourceKey);
    if (isPermissionValue(sourceMetadata[metadataKey])) return sourceMetadata[metadataKey];

    // Backward compatibility for existing flat metadata permissions.
    if (isPermissionValue(metadata[metadataKey])) return metadata[metadataKey];

    return undefined;
  };

  const getMetadataPermissionState = (sourceKey, metadataKey) => {
    const value = getMetadataPermissionValue(sourceKey, metadataKey);
    if (value === true) return "true";
    if (value === false) return "false";
    return "notset";
  };

  // Handle metadata wildcard toggle
  const handleMetadataWildcardToggle = (checked) => {
    setPermissions((prev) => {
      if (checked) {
        return prev;
      } else {
        // Remove wildcard
        const newMetadata = { ...prev.metadata };
        delete newMetadata["*"];
        return {
          ...prev,
          metadata: newMetadata,
        };
      }
    });
  };

  // Check if metadata wildcard is selected
  const isMetadataWildcardSelected = () => {
    return permissions.metadata?.["*"] === true;
  };

  const isMetadataAllowOptionDisabled = (sourceKey, metadataKey) => {
    return wouldAllowAllMetadataCategories(
      permissions.metadata || {},
      sourceKey,
      metadataKey
    );
  };

  // Handle comment action permission change (true/false/undefined)
  const handleCommentChange = (actionKey, val) => {
    setPermissions((prev) => {
      const newComments = { ...prev.comments };
      if (val === "true") {
        newComments[actionKey] = true;
      } else if (val === "false") {
        newComments[actionKey] = false;
      } else {
        delete newComments[actionKey];
      }
      return { ...prev, comments: newComments };
    });
  };

  // Get comment action permission state (for Select dropdown)
  const getCommentState = (actionKey) => {
    const val = permissions.comments?.[actionKey];
    if (val === true) return "true";
    if (val === false) return "false";
    return "notset";
  };

  // Get count of selected items (only "Allow" true, not "Deny" false)
  const getSelectedCount = () => {
    const pagesForBuilder = getPagesForBuilder();
    const categoriesForBuilder = getReportCategoriesForBuilder();
    const systemsForBuilder = getReportSystemPermissionsForBuilder();

    const hasPageWildcard = isPageWildcardSelected();
    const pageCount = hasPageWildcard
      ? pagesForBuilder.length
      : Object.keys(permissions.page || {}).filter(
          (k) => permissions.page[k] === true && k !== "*"
        ).length;

    const hasCategoryWildcard = isReportCategoryWildcardSelected();
    const categoryCount = hasCategoryWildcard
      ? categoriesForBuilder.length
      : Object.keys(permissions.reportCategory || {}).filter(
          (k) => permissions.reportCategory[k] === true && k !== "*"
        ).length;

    const hasSystemWildcard = isReportSystemWildcardSelected();
    const reportSystemCount = hasSystemWildcard
      ? systemsForBuilder.length
      : Object.keys(permissions.reportSystem || {}).filter(
          (k) => permissions.reportSystem[k] === true && k !== "*"
        ).length;

    // Count modules with at least one allowed subpage
    const subpageCount = Object.keys(permissions.subpage || {}).filter(module => {
      const moduleSubpages = permissions.subpage[module];
      return Object.keys(moduleSubpages).some(key =>
        key === "*" ? moduleSubpages[key] === true : moduleSubpages[key] === true
      );
    }).length;

    const hasMetadataWildcard = isMetadataWildcardSelected();
    const metadataCount = hasMetadataWildcard
      ? getSelectableMetadataCategoryCount()
      : getAllowedMetadataCategoryCount();

    const commentsCount = Object.values(permissions.comments || {}).filter(v => v === true).length;

    return { pageCount, categoryCount, reportSystemCount, subpageCount, metadataCount, commentsCount };
  };

  const selectedCounts = getSelectedCount();

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header with JSON preview toggle */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">Permissions Builder</Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Chip
            label={`${selectedCounts.pageCount} Pages`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${selectedCounts.categoryCount} Report Categories`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${selectedCounts.reportSystemCount} Report Suites`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${Object.keys(permissions.subpage || {}).length} Module Subpages`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${selectedCounts.metadataCount} Metadata`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${selectedCounts.commentsCount} Comment Actions`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Tooltip title="View JSON Preview">
            <IconButton
              size="small"
              onClick={() => setShowJsonPreview(!showJsonPreview)}
              color={showJsonPreview ? "primary" : "default"}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* JSON Preview */}
      {showJsonPreview && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: "#f5f5f5",
            maxHeight: "300px",
            overflow: "auto",
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            JSON Preview:
          </Typography>
          <TextField
            fullWidth
            multiline
            value={jsonPreview}
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: "monospace",
                fontSize: "0.85rem",
              },
            }}
            variant="outlined"
            size="small"
          />
        </Paper>
      )}

      {/* Tabs for different permission types */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Pages" />
          <Tab label="Reports" />
          <Tab label="Subpages" />
          <Tab label="Metadata" />
          <Tab label="Comments" />
        </Tabs>

        <Divider />

        {/* Pages Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 2, maxHeight: "500px", overflow: "auto" }}>
       
            
            {/* Wildcard option for all pages */}
            <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: "divider" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPageWildcardSelected()}
                    onChange={(e) =>
                      handlePageWildcardToggle(e.target.checked)
                    }
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      All Pages (*)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Grants access to all pages in the application
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {Object.keys(pagesByModule)
              .sort()
              .map((module) => {
                const hasWildcard = isPageWildcardSelected();
                const modulePages = pagesByModule[module];
                // Count only "Allow" (true) permissions, not "Deny" (false)
                const selectedCount = hasWildcard 
                  ? modulePages.length 
                  : modulePages.filter(page => permissions.page?.[page.key] === true).length;
                return (
                  <Accordion key={module}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {module} ({modulePages.length} pages{selectedCount > 0 ? ` - ${selectedCount} selected` : ''})
                        </Typography>
                        {hasWildcard && (
                          <Chip label="All (*)" size="small" color="primary" />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {pagesByModule[module].map((page) => (
                          <Box key={page.key} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2">
                                {page.displayName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {page.key}
                              </Typography>
                            </Box>
                            <FormControl size="small" sx={{ minWidth: 120 }} disabled={hasWildcard}>
                              <Select
                                value={hasWildcard ? "true" : getPagePermissionState(page.key)}
                                onChange={(e) =>
                                  handlePageChange(page.key, e.target.value)
                                }
                                disabled={hasWildcard}
                              >
                                <MenuItem value="notset">Not Set</MenuItem>
                                <MenuItem value="true">Allow</MenuItem>
                                <MenuItem value="false">Deny</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
          </Box>
        )}

        {/* Reports Tab (Report Categories + Report Suites) */}
        {activeTab === 1 && (
          <Box sx={{ p: 2, maxHeight: "500px", overflow: "auto" }}>

            {/* Report Suites Section */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Report Suites
            </Typography>

            {/* Wildcard option for all report suites */}
            <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: "divider" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isReportSystemWildcardSelected()}
                    onChange={(e) =>
                      handleReportSystemWildcardToggle(e.target.checked)
                    }
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      All Report Suites (*)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Grants access to all ERP report system tabs
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {getReportSystemPermissionsForBuilder()
                .sort((a, b) =>
                  a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" })
                )
                .map((system) => {
                  const hasWildcard = isReportSystemWildcardSelected();
                  return (
                    <Box key={system.key} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">{system.displayName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {system.key}
                        </Typography>
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 120 }} disabled={hasWildcard}>
                        <Select
                          value={hasWildcard ? "true" : getReportSystemPermissionState(system.key)}
                          onChange={(e) =>
                            handleReportSystemChange(system.key, e.target.value)
                          }
                          disabled={hasWildcard}
                        >
                          <MenuItem value="notset">Not Set</MenuItem>
                          <MenuItem value="true">Allow</MenuItem>
                          <MenuItem value="false">Deny</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  );
                })}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Report Categories Section */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Report Categories
            </Typography>

            {/* Wildcard option for all report categories */}
            <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: "divider" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isReportCategoryWildcardSelected()}
                    onChange={(e) =>
                      handleReportCategoryWildcardToggle(e.target.checked)
                    }
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      All Report Categories (*)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Grants access to all report categories
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {getReportCategoriesForBuilder()
                .filter(category => {
                  // Report categories are keyed by module name (e.g., 'HR', 'AP', 'GL')
                  // Check if the category's key (which is the module) is enabled
                  if (modulesLoading) return false;
                  return isModuleEnabled(category.key, enabledModules);
                })
                .sort((a, b) =>
                  a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })
                )
                .map((category) => {
                const hasWildcard = isReportCategoryWildcardSelected();
                return (
                  <Box key={category.key} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">
                        {category.displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {category.key}
                      </Typography>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 120 }} disabled={hasWildcard}>
                      <Select
                        value={hasWildcard ? "true" : getReportCategoryPermissionState(category.key)}
                        onChange={(e) =>
                          handleReportCategoryChange(category.key, e.target.value)
                        }
                        disabled={hasWildcard}
                      >
                        <MenuItem value="notset">Not Set</MenuItem>
                        <MenuItem value="true">Allow</MenuItem>
                        <MenuItem value="false">Deny</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Subpages Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 2, maxHeight: "500px", overflow: "auto" }}>

            {Object.keys(SUBPAGES)
              .filter(module => {
                // Only show modules that are enabled
                return !modulesLoading && isModuleEnabled(module, enabledModules);
              })
              .sort()
              .map((module) => {
                const moduleSubpages = getSubpagesForBuilder(module)
                  .sort((a, b) => 
                    a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })
                  );
                const hasWildcard = isSubpageWildcardSelected(module);
                
                // Skip modules with no subpages that should show in builder
                if (moduleSubpages.length === 0) {
                  return null;
                }
                
                // Count only "Allow" (true) permissions, not "Deny" (false)
                const selectedCount = hasWildcard 
                  ? moduleSubpages.length 
                  : moduleSubpages.filter(subpage => {
                      const moduleSubpagesObj = permissions.subpage?.[module];
                      return moduleSubpagesObj?.[subpage.key] === true;
                    }).length;
                
                return (
                  <Accordion key={module}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {module} ({moduleSubpages.length} subpages{selectedCount > 0 ? ` - ${selectedCount} selected` : ''})
                        </Typography>
                        {hasWildcard && (
                          <Chip label="All (*)" size="small" color="primary" />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {/* Wildcard option */}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={hasWildcard}
                              onChange={(e) =>
                                handleSubpageWildcardToggle(
                                  module,
                                  e.target.checked
                                )
                              }
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                All Subpages (*)
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Grants access to all subpages in {module}
                              </Typography>
                            </Box>
                          }
                        />
                        <Divider sx={{ my: 1 }} />
                        {/* Individual subpages */}
                        {moduleSubpages.map((subpage) => (
                          <Box key={subpage.key} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2">
                                {subpage.displayName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {subpage.key}
                              </Typography>
                            </Box>
                            <FormControl size="small" sx={{ minWidth: 120 }} disabled={hasWildcard}>
                              <Select
                                value={hasWildcard ? "true" : getSubpagePermissionState(module, subpage.key)}
                                onChange={(e) =>
                                  handleSubpageChange(module, subpage.key, e.target.value)
                                }
                                disabled={hasWildcard}
                              >
                                <MenuItem value="notset">Not Set</MenuItem>
                                <MenuItem value="true">Allow</MenuItem>
                                <MenuItem value="false">Deny</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            {!modulesLoading && Object.keys(SUBPAGES)
              .filter(module => {
                return isModuleEnabled(module, enabledModules) && 
                       getSubpagesForBuilder(module).length > 0;
              })
              .length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No subpages available for enabled modules.
              </Typography>
            )}
          </Box>
        )}

        {/* Metadata Tab */}
        {activeTab === 3 && (
          <Box sx={{ p: 2, maxHeight: "500px", overflow: "auto" }}>

            
            {metadataLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Loading metadata categories...
                </Typography>
              </Box>
            ) : (
              <>
                {/* Wildcard option for all metadata categories */}
                <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: "divider" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isMetadataWildcardSelected()}
                        onChange={(e) =>
                          handleMetadataWildcardToggle(e.target.checked)
                        }
                        disabled={!isMetadataWildcardSelected()}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          All Metadata Categories (*)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {isMetadataWildcardSelected()
                            ? "Clear this to remove all metadata category access"
                            : "All metadata category access is disabled; choose individual categories instead"}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {metadataCategories.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No metadata categories available.
                    </Typography>
                  ) : (
                    metadataGroups.map(({ system, categories }) => {
                      const hasGlobalWildcard = isMetadataWildcardSelected();
                      const selectedCount = hasGlobalWildcard
                        ? categories.length
                        : categories.filter((category) => {
                            const categoryName = getCategoryName(category);
                            return (
                              categoryName &&
                              getMetadataPermissionValue(system.sourceKey, categoryName) === true
                            );
                          }).length;

                      return (
                        <Accordion key={system.sourceKey}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {system.label} ({categories.length} categories{selectedCount > 0 ? ` - ${selectedCount} selected` : ""})
                              </Typography>
                              {hasGlobalWildcard && (
                                <Chip label="All (*)" size="small" color="primary" />
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              {categories.map((category) => {
                                const categoryName = getCategoryName(category);
                                const categoryDescription = getCategoryDescription(category);
                                const disabled = hasGlobalWildcard;
                                const allowDisabled = isMetadataAllowOptionDisabled(
                                  system.sourceKey,
                                  categoryName
                                );

                                return (
                                  <Box
                                    key={`${system.sourceKey}:${categoryName}`}
                                    sx={{ display: "flex", alignItems: "center", gap: 2 }}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="body2">
                                        {categoryDescription}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {categoryName}
                                      </Typography>
                                    </Box>
                                    <FormControl size="small" sx={{ minWidth: 120 }} disabled={disabled}>
                                      <Select
                                        value={
                                          disabled
                                            ? "true"
                                            : getMetadataPermissionState(system.sourceKey, categoryName)
                                        }
                                        onChange={(e) =>
                                          handleMetadataChange(
                                            system.sourceKey,
                                            categoryName,
                                            e.target.value
                                          )
                                        }
                                        disabled={disabled}
                                      >
                                        <MenuItem value="notset">Not Set</MenuItem>
                                        <MenuItem value="true" disabled={allowDisabled}>Allow</MenuItem>
                                        <MenuItem value="false">Deny</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Box>
                                );
                              })}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })
                  )}
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Comments Tab */}
        {activeTab === 4 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Controls who can interact with record comments across all tiles (Employee, Vendor, and others added later).
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {COMMENT_ACTIONS.map((action) => (
                <Box key={action.key} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{action.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {action.description}
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={getCommentState(action.key)}
                      onChange={(e) => handleCommentChange(action.key, e.target.value)}
                    >
                      <MenuItem value="notset">Not Set</MenuItem>
                      <MenuItem value="true">Allow</MenuItem>
                      <MenuItem value="false">Deny</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              ))}
            </Box>
          </Box>
        )}

      </Paper>
    </Box>
  );
}

